import { createContext, useContext, useState, useMemo } from 'react';

const AnnotationsStateContext = createContext(null);

export const useAnnotationsState = () => {
  const context = useContext(AnnotationsStateContext);
  if (!context) {
    throw new Error('useAnnotationsState must be used within AnnotationsStateProvider');
  }
  return context;
};

export const AnnotationsStateProvider = ({ children }) => {
  // Annotation UI state
  const [selectedElementForAnnotation, setSelectedElementForAnnotation] = useState(null);
  const [editingNote, setEditingNote] = useState(null);

  // Scene numbers (for manual editing)
  const [sceneNumbers, setSceneNumbers] = useState(() => {
    try {
      const stored = localStorage.getItem('screenwriter_scene_numbers');
      return stored ? JSON.parse(stored) : {};
    } catch {
      return {};
    }
  });

  const actions = useMemo(() => ({
    // Selected element for annotations
    setSelectedElementForAnnotation: (element) => {
      setSelectedElementForAnnotation(element);
    },

    clearSelectedElement: () => {
      setSelectedElementForAnnotation(null);
    },

    // Note editing
    setEditingNote: (note) => {
      setEditingNote(note);
    },

    clearEditingNote: () => {
      setEditingNote(null);
    },

    // Scene numbers
    updateSceneNumber: (sceneId, sceneNumber) => {
      setSceneNumbers(prev => {
        const updated = { ...prev, [sceneId]: sceneNumber };
        localStorage.setItem('screenwriter_scene_numbers', JSON.stringify(updated));
        return updated;
      });
    },

    deleteSceneNumber: (sceneId) => {
      setSceneNumbers(prev => {
        const updated = { ...prev };
        delete updated[sceneId];
        localStorage.setItem('screenwriter_scene_numbers', JSON.stringify(updated));
        return updated;
      });
    },

    clearAllSceneNumbers: () => {
      setSceneNumbers({});
      localStorage.removeItem('screenwriter_scene_numbers');
    }
  }), []);

  const value = useMemo(() => ({
    state: {
      selectedElementForAnnotation,
      editingNote,
      sceneNumbers
    },
    actions
  }), [selectedElementForAnnotation, editingNote, sceneNumbers, actions]);

  return (
    <AnnotationsStateContext.Provider value={value}>
      {children}
    </AnnotationsStateContext.Provider>
  );
};
